import React from 'react';
import { 
  Paper, Box, Typography, Button, List, ListItem, 
  ListItemText, ListItemSecondaryAction, IconButton,
  Tooltip, ButtonGroup, Chip, Badge, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'; // Import delete icon
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAudioPlayer } from '../../context/AudioPlayerContext'; // Import AudioPlayer context

// Helper function to count words in a string
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// Helper function to format content preview with word count
const formatContentPreview = (content) => {
  if (!content) return 'No content';
  
  const wordCount = countWords(content);
  const preview = content.substring(0, 100) + (content.length > 100 ? '...' : '');
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
      <Typography variant="body2" component="span">
        {preview}
      </Typography>
      <Chip 
        label={`${wordCount} words`}
        size="small"
        variant="outlined"
        sx={{ 
          mt: 1, 
          height: 20, 
          fontSize: '0.7rem',
          color: 'text.secondary',
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }}
      />
    </Box>
  );
};

// Calculate total words for a level
const calculateLevelWordCount = (blocks) => {
  if (!blocks || blocks.length === 0) return 0;
  return blocks.reduce((sum, block) => sum + countWords(block.content), 0);
};

export default function BlocksPanel({
  activeThemeId,
  themes,
  blocksByLevel,
  onBlockAdd,
  onBlockEdit,
  onBlockDelete,
  onBlockReorder,
  onGenerateContent,
  onGenerateAudio,
  onDeleteAudio, // Add this new prop
  onGenerateAllAudio // Add this new prop
}) {
  const activeTheme = themes.find(t => t.id === activeThemeId);
  const { playTrack } = useAudioPlayer(); // Get playTrack function from context
  
  // Handler to play audio from a block
  const handlePlayAudio = (block) => {
    if (block.audioUrl) {
      playTrack({
        url: block.audioUrl,
        title: `${block.title} (Level ${block.level})`,
        artist: activeTheme ? activeTheme.title : 'Course block'
      });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Content Blocks
          {activeTheme && (
            <span> for {activeTheme.title}</span>
          )}
        </Typography>
        {activeThemeId && (
          <Box>
            <ButtonGroup>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={onBlockAdd}
              >
                Add Block
              </Button>
              <Tooltip title="Generate content blocks using AI">
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={onGenerateContent}
                >
                  Generate Content
                </Button>
              </Tooltip>
              {/* Add new button for bulk audio generation */}
              <Tooltip title="Generate audio for all blocks in this theme">
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<VolumeUpIcon />}
                  onClick={onGenerateAllAudio}
                  disabled={Object.keys(blocksByLevel).length === 0}
                >
                  Generate All Audio
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        )}
      </Box>
      
      {!activeThemeId && (
        <Typography color="text.secondary" align="center" py={4}>
          Please select a theme first to manage its content blocks
        </Typography>
      )}
      
      {activeThemeId && Object.keys(blocksByLevel).length === 0 && (
        <Typography color="text.secondary" align="center" py={4}>
          No blocks in this theme yet. Add your first content block.
        </Typography>
      )}
      
      {activeThemeId && Object.keys(blocksByLevel).map(level => {
        const levelWordCount = calculateLevelWordCount(blocksByLevel[level]);
        
        return (
          <Box key={level} mb={4}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6" mr={2}>
                Level {level} Content
              </Typography>
              <Chip 
                label={`${levelWordCount} words total`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <DragDropContext onDragEnd={onBlockReorder}>
              <Droppable droppableId={`level-${level}`}>
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {blocksByLevel[level].map((block, index) => (
                      <Draggable key={block.id} draggableId={`block-${block.id}-${block.level}`} index={index}>
                        {(provided) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ 
                              border: '1px solid #eee', 
                              mb: 1, 
                              borderRadius: 1,
                              // Add subtle highlight for blocks with audio
                              ...(block.audioUrl && {
                                borderLeft: '4px solid #3f51b5'
                              })
                            }}
                          >
                            <div {...provided.dragHandleProps} style={{ marginRight: 10 }}>
                              <DragIndicatorIcon />
                            </div>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center">
                                  <Typography variant="subtitle1">{block.title}</Typography>
                                  {block.audioUrl && (
                                    <Chip 
                                      size="small" 
                                      label="Audio Available" 
                                      color="primary" 
                                      variant="outlined"
                                      icon={<VolumeUpIcon fontSize="small" />}
                                      sx={{ ml: 1, height: 24 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={formatContentPreview(block.content)}
                            />
                            
                            {/* Create a dedicated audio controls group with visual separation */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                borderLeft: '1px solid #eee',
                                pl: 1,
                                mr: 1
                              }}
                            >
                              {/* Audio Controls Group */}
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {block.audioUrl ? (
                                  <>
                                    <Tooltip title="Play audio">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handlePlayAudio(block)}
                                        color="primary"
                                        sx={{ mx: 0.5 }}
                                      >
                                        <PlayArrowIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete audio file">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => onDeleteAudio(block.id)}
                                        color="error"
                                        sx={{ mx: 0.5 }}
                                      >
                                        <DeleteForeverIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <Tooltip title="Generate audio for this block">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => onGenerateAudio(block)}
                                      color="primary"
                                      sx={{ mx: 0.5 }}
                                    >
                                      <VolumeUpIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            
                            {/* Content management buttons */}
                            <ListItemSecondaryAction>
                              <Tooltip title="Edit block content">
                                <IconButton edge="end" onClick={() => onBlockEdit(block)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete block">
                                <IconButton edge="end" onClick={() => onBlockDelete(block.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        );
      })}
    </Paper>
  );
}
